// timelineMerge — Anchored Timeline v1 の Web 共通マージユーティリティ。
//
// 正本仕様: docs/chat_timeline_unification_spec_20260703.md §3.2 単一マージ関数。
//
// 前提: Web のメッセージ配列は「DESC(新→旧)、index0 = 最新」+ CSS column-reverse。
// 本ユーティリティは DESC 配列を受け取り DESC 配列を返す。
//
// 契約(§3.2):
//   1. id 正規化(string 化)。id 無しは dedupe キー(created_at+type+hash 等)で代替。
//   2. 既存 id は「フィールド上書きマージ」(後勝ち)。ただし read_count のみ
//      Math.max による単調増加(stale payload での既読数巻き戻り防止)。
//   3. 新規 id は挿入し、(created_at, id) で安定ソート(DESC = 新しい方が先頭)。
//   4. 戻り値は参照安定(変化なしなら同一参照を返し再描画抑制)。
//
// 削除は removeMessages(ids)、部分更新(既読/リアクション)は patchMessages(ids, patch)。

export const normalizeMsgId = (value) => (value == null ? '' : String(value).trim());

// 既存ページの getMessageDedupKey と互換のデフォルトキー生成。
// id があれば `id:<id>`、無ければ主要フィールドの合成キー。
export const defaultMessageKey = (message) => {
  const id = normalizeMsgId(message?.id);
  if (id) return `id:${id}`;
  return [
    'fallback',
    message?.message_type || message?.messageType || '',
    message?.message_format || message?.messageFormat || '',
    message?.created_at || message?.createdAt || '',
    message?.admin_user_id || message?.adminUserId || '',
    message?.artist_id || message?.artistId || '',
    message?.image_id || message?.imageId || '',
    message?.file_id || message?.fileId || '',
    String(message?.content || '').trim(),
  ].join(':');
};

const toTime = (message) => {
  const raw = message?.created_at ?? message?.createdAt ?? 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
};

// (created_at, id) 昇順の論理順。DESC 配列にするため比較を反転して使う。
// id は数値であれば数値比較、そうでなければ文字列比較(安定タイブレーク)。
const compareLogicalAsc = (a, b) => {
  const ta = toTime(a);
  const tb = toTime(b);
  if (ta !== tb) return ta - tb;
  const ia = normalizeMsgId(a?.id);
  const ib = normalizeMsgId(b?.id);
  const na = Number(ia);
  const nb = Number(ib);
  if (ia !== '' && ib !== '' && Number.isFinite(na) && Number.isFinite(nb) && String(na) === ia && String(nb) === ib) {
    if (na !== nb) return na - nb;
  }
  if (ia < ib) return -1;
  if (ia > ib) return 1;
  return 0;
};

// DESC 比較(新しい方が先頭)。
const compareDesc = (a, b) => compareLogicalAsc(b, a);

// フィールド上書きマージ(後勝ち)。ただし read_count のみ単調増加を保証する:
// 既読数は減らない性質のため、遅延して届いた stale payload による巻き戻りを防ぐ
// (グループ/イベントの既読数表示の既存仕様。§3.2-2)。
const mergeMessagePair = (prevMsg, msg) => {
  const merged = { ...prevMsg, ...msg };
  const prevCount = Number(prevMsg?.read_count);
  const nextCount = Number(msg?.read_count);
  const hasPrev = Number.isFinite(prevCount);
  const hasNext = Number.isFinite(nextCount);
  if (hasPrev || hasNext) {
    merged.read_count = Math.max(hasPrev ? prevCount : 0, hasNext ? nextCount : 0);
  }
  return merged;
};

// 2つのメッセージが「実質同一」か(浅い比較)。フィールド差分検出で参照安定化に使う。
const isShallowEqualMessage = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (let i = 0; i < keysA.length; i += 1) {
    const k = keysA[i];
    if (a[k] !== b[k]) return false;
  }
  return true;
};

/**
 * mergeMessages — 複数の流入を1つのタイムライン配列へ統合する。
 *
 * 出力は常に DESC(新→旧、index0=最新)。Web 全チャットページの正準方向で、
 * wire 順(イベントは ASC、個人/グループは DESC — §1)に関わらずソートで吸収する。
 *
 * @param {Array} current  既存配列(DESC)
 * @param {Array} incoming 追加/更新するメッセージ配列(順序不問)
 * @param {Object} [options]
 * @param {(m:any)=>string} [options.getKey] dedupe キー生成(既定: defaultMessageKey)
 * @returns {Array} DESC 配列。内容が変化しなければ current と同一参照を返す。
 */
export const mergeMessages = (current = [], incoming = [], options = {}) => {
  const getKey = typeof options.getKey === 'function' ? options.getKey : defaultMessageKey;
  const base = Array.isArray(current) ? current : [];
  const additions = Array.isArray(incoming) ? incoming : [];
  if (additions.length === 0) return base;

  // 既存をキー→{index, message} で索引。
  const indexByKey = new Map();
  for (let i = 0; i < base.length; i += 1) {
    const key = getKey(base[i]);
    if (key && !indexByKey.has(key)) indexByKey.set(key, i);
  }

  let mutated = false;
  // next は base をコピーせず、変更が確定するまで base を参照し続ける(参照安定化)。
  let next = base;
  const ensureCopy = () => {
    if (next === base) next = base.slice();
  };

  const appended = [];
  const seenIncoming = new Set();

  for (let i = 0; i < additions.length; i += 1) {
    const msg = additions[i];
    const key = getKey(msg);
    if (!key) continue;
    if (indexByKey.has(key)) {
      // 既存: フィールド上書きマージ(後勝ち、read_count のみ単調増加)。変化がある時だけコピー。
      const idx = indexByKey.get(key);
      const prevMsg = next[idx];
      const mergedMsg = mergeMessagePair(prevMsg, msg);
      if (!isShallowEqualMessage(prevMsg, mergedMsg)) {
        ensureCopy();
        next[idx] = mergedMsg;
        mutated = true;
      }
    } else if (!seenIncoming.has(key)) {
      // 新規: incoming 内の重複も1件に畳む(最後勝ち)。
      seenIncoming.add(key);
      appended.push(msg);
    } else {
      // incoming 内で既出の新規キー → 後勝ちで上書き(read_count のみ単調増加)
      const existingIdx = appended.findIndex((m) => getKey(m) === key);
      if (existingIdx >= 0) appended[existingIdx] = mergeMessagePair(appended[existingIdx], msg);
    }
  }

  if (appended.length > 0) {
    ensureCopy();
    // 追加分を連結してから全体を安定ソート(DESC、必要時のみ)。
    for (let i = 0; i < appended.length; i += 1) next.push(appended[i]);
    next.sort(compareDesc);
    mutated = true;
  }

  return mutated ? next : base;
};

/**
 * removeMessages — 指定 id 群をタイムラインから除去。
 * スクロール位置に影響を与えないこと(§3.3 SOCKET_DEL)。
 * @returns {Array} 変化なしなら current と同一参照。
 */
export const removeMessages = (current = [], ids = []) => {
  const base = Array.isArray(current) ? current : [];
  const idList = Array.isArray(ids) ? ids : [ids];
  const removeSet = new Set(idList.map((id) => normalizeMsgId(id)).filter(Boolean));
  if (removeSet.size === 0) return base;
  let mutated = false;
  const next = base.filter((m) => {
    const hit = removeSet.has(normalizeMsgId(m?.id));
    if (hit) mutated = true;
    return !hit;
  });
  return mutated ? next : base;
};

/**
 * patchMessages — 指定 id 群へ部分更新(既読/リアクション等)を適用。
 * 並び順・スクロール位置に影響を与えないこと(§3.3 READ/REACTION)。
 * @param {Array} current DESC 配列
 * @param {Array|string} ids 対象 id 群
 * @param {Object|((m:any)=>Object)} patch 適用するフィールド、または (message)=>partial を返す関数
 * @returns {Array} 変化なしなら current と同一参照。
 */
export const patchMessages = (current = [], ids = [], patch = {}) => {
  const base = Array.isArray(current) ? current : [];
  const idList = Array.isArray(ids) ? ids : [ids];
  const targetSet = new Set(idList.map((id) => normalizeMsgId(id)).filter(Boolean));
  if (targetSet.size === 0) return base;
  const computePatch = typeof patch === 'function' ? patch : () => patch;

  let mutated = false;
  let next = base;
  for (let i = 0; i < base.length; i += 1) {
    const msg = base[i];
    if (!targetSet.has(normalizeMsgId(msg?.id))) continue;
    const partial = computePatch(msg) || {};
    const updated = { ...msg, ...partial };
    if (isShallowEqualMessage(msg, updated)) continue;
    if (next === base) next = base.slice();
    next[i] = updated;
    mutated = true;
  }
  return mutated ? next : base;
};
